import {
  getVariantAttributeLabel,
  nonEmpty,
  type ExpandedRichText,
  type ProductVariantAvailabilityStatus,
} from "@swo/shared";
import { SwarovskiOptikLocaleFrontend, type GeneralTranslation } from "@swo/storefront";
import { nonEmptyArray as NEA, option as O } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import type React from "react";
import { useCallback, useState } from "react";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { useGeneralTranslationsContext } from "../../global/hooks/use-general-translations-context";
import { useCtxFns, usePageLocaleContext } from "../../global/page-context";
import { ComponentRenderer } from "../_layout-helpers/component-renderer";
import { BDPVariantPicker } from "./BDPVariantPicker";
import { PDPBundleContents } from "./PDPBundleContents";
import { PDPGalleryModalRoot } from "./PDPGalleryModalRoot";
import { BDPMetadata } from "./PDPMetadata";
import { PDPModal } from "./PDPModal";
import { ProductDetailPageDescription } from "./PDP_Description/ProductDetailPageDescription";
import { usePdpGalleryWithLightbox } from "./PDP_Lightbox/use-pdp-gallery-with-lightbox";
import { PDPProductDetailsIntro } from "./PDP_SingleImageCarousel/PDPAboveTheFoldLayout";
import { PDPAboveTheFoldLayoutWrapper } from "./PDP_SingleImageCarousel/PDPAboveTheFoldLayoutWrapper";
import { PDPBundleTeaser } from "./PDP_SingleImageCarousel/PDPBundleTeaser";
import { PDPDetailsWrapper } from "./PDP_SingleImageCarousel/PDPDetailsWrapper";
import { PDPImageWithPlaceholder } from "./PDP_SingleImageCarousel/PDPImageWithPlaceholder";
import { getGalleryProps } from "./PDP_SingleImageCarousel/PDPStickyGallery";
import { ProductDetailsIntro } from "./PDP_SingleImageCarousel/ProductDetailsIntro";
import { computePDPAvailabilityBarState } from "./PDP_SingleImageCarousel/availability/pdp-availability";
import { PDPStickyPriceBar } from "./PDP_SingleImageCarousel/price-bar/PDPStickyPriceBar";
import { getPDPStockLevelIndicator2 } from "./PDP_SingleImageCarousel/price-bar/PDPStockLevelIndicator";
import { getBundleSelectionParams } from "./getBundleSelectionParams";
import { getImages } from "./getImages";
import { getPDPActions } from "./getPDPActions";
import { PDPDeliveryPackageDescription } from "./pdp-delivery-package-description";
import type { BundlePageProps, PageProps } from "./product-detail-page-layout";
import { renderNonEmpty } from "./renderNonEmpty";
import { outOfStock } from "./state/get-product-availability";
import { useCurrentVariantsSelection } from "./useCurrentVariantsSelection";
import { getPdpStickyDesktopButton } from "./PDP_SingleImageCarousel/price-bar/GetPdpStickyDesktopButton";
import { PDPScrollingGallery } from "./PDP_Gallery/PDPScrollingGallery";
import { PDPSectionsWithNavigation, type PDPSections } from "./pdp-navigation";
import { usePdpTranslations } from "./PdpTranslationsContext";
import type { Translated } from "../../domain/translations/translations";
import type { ReviewTranslations } from "./reviewTranslations";
import type { HeaderPinnedState } from "../../components/HeaderComponent/subcomponents/use-header-pinned-state";

import type { NonEmptyArray } from "fp-ts/lib/NonEmptyArray";
import { ProductDetailPageDownloadsCarousel } from "./PDP_DownloadsCarousel/ProductDetailPageDownloadsCarousel";

export const BDPMainComponent: React.FC<{
  data: Omit<BundlePageProps["data"], "header" | "footer">;
  PdpInformationSet: React.ReactNode;
  ContactDataComponent: React.ReactNode;
  page: PageProps["page"];
  locale: SwarovskiOptikLocaleFrontend;
  headerState: HeaderPinnedState;
  reviewsT: ReviewTranslations;
  children?: React.ReactNode;
}> = ({ data, PdpInformationSet, ContactDataComponent, page, locale, headerState, reviewsT, children }) => {
  const availabilityMap = Array.isArray(data.availability)
    ? new Map<string, ProductVariantAvailabilityStatus>(data.availability.map(a => [a.sku, a.status]))
    : data.availability;

  const { state: currentVariantsState, onBundleElementChanged } = useCurrentVariantsSelection(
    data.selectedProduct,
    availabilityMap,
  );

  const { price, availability, isCheckoutable } = getBundleSelectionParams(currentVariantsState, availabilityMap);

  const [isModalOpen, setModalOpen] = useState(false);
  const hideModalCallback = useCallback(() => {
    setModalOpen(false);
  }, []);

  const galleryConfig = getGalleryProps(data.selectedProduct, null, 10);
  const galleryWithLightbox = usePdpGalleryWithLightbox(galleryConfig);

  const { addToCart: addToCart_, areCartActionsLocked } = getPDPActions(
    setModalOpen,
    data.selectedProduct.id,
    null,
    data.links,
  );

  const addToCart = () => addToCart_({ tag: "bdp", selectedVariants: currentVariantsState.map(v => v.value) });

  const availabilityState = computePDPAvailabilityBarState(
    { tag: "impossible" },
    availability ?? outOfStock(),
    addToCart,
    locale,
    data.translations.pdp,
    data.links,
    undefined,
    null,
  );

  const primaryButton = getPdpStickyDesktopButton(availabilityState.action, areCartActionsLocked);

  const deliveryPackageProps = pipe(data.selectedProduct.deliveryPackage, NEA.fromArray, O.toUndefined);
  const longDescription = data.selectedProduct.longDescription;

  const { showPrice } = useCtxFns();
  const gt = useGeneralTranslationsContext();

  const bundleContents = currentVariantsState.map(({ value }) => {
    const attributesList = value.attributes.unique
      .map(a =>
        getVariantAttributeLabel(
          a,
          {
            noLabel: data.translations.techSpec.noLabel.value,
            yesLabel: data.translations.techSpec.yesLabel.value,
          },
          locale.country === "INTERNATIONAL" ? "AT" : locale.country.isoCode,
        ),
      )
      .filter(nonEmpty);

    return {
      headline: value.product.name,
      attributes: attributesList,
      image: [...getImages(value.images), ...getImages(value.product.images)][0],
      link: value.path,
      price: value.price,
      shortDescription: value.product.shortDescription,
    };
  });

  return (
    <>
      <BDPMetadata
        selectedProduct={data.selectedProduct}
        seoMetadata={data.seoMetadata}
        taxonomy={data.taxonomy}
        page={page}
      />

      <PDPModal isModalOpen={isModalOpen} hideModalCallback={hideModalCallback} />

      <PDPAboveTheFoldLayoutWrapper>
        <PDPImageWithPlaceholder galleryWithLightbox={galleryWithLightbox} availability={availability} />

        <PDPDetailsWrapper
          Element={
            <PDPStickyPriceBar
              primaryButton={primaryButton("normal")}
              extension={availabilityState.extension}
              priceLabel={showPrice(price)}
              stockLevel={getPDPStockLevelIndicator2(availability)}
            />
          }
        >
          <ProductDetailsIntro
            breadcrumbs={data.taxonomy.breadcrumbs}
            isNew={data.selectedProduct.isNew}
            name={data.selectedProduct.name}
            SubheadlineElement={null}
          />

          <ProductDetailPageDescription productShortDescription={data.selectedProduct.shortDescription} />

          {typeof availabilityMap !== "string" ? (
            <BDPVariantPicker
              products={data.selectedProduct.products}
              currentVariantsState={currentVariantsState}
              onBundleElementChanged={onBundleElementChanged}
              countryIsoCode={locale.country === "INTERNATIONAL" ? "AT" : locale.country.isoCode}
              translations={data.translations.techSpec}
              priceLabel={showPrice(price)}
              availabilityMap={availabilityMap}
            />
          ) : null}
        </PDPDetailsWrapper>

        <PDPProductDetailsIntro>
          {PdpInformationSet}
          <PDPBundleTeaser bundles={data.related.bundles} />
          <PDPScrollingGallery galleryWithLightbox={galleryWithLightbox} />
        </PDPProductDetailsIntro>

        <PDPGalleryModalRoot galleryWithLightbox={galleryWithLightbox} />
      </PDPAboveTheFoldLayoutWrapper>

      {/* <Spacer size={"lg"} /> */}

      <BDPBelowTheFoldLayout
        data={data}
        gt={gt}
        reviewsT={reviewsT}
        primaryButton={primaryButton("small")}
        headerState={headerState}
        deliveryPackageProps={deliveryPackageProps}
        longDescription={longDescription}
        BundleContents={<PDPBundleContents elements={bundleContents} />}
      />

      {children}

      <Spacer size={"lg"} />
      {ContactDataComponent}
    </>
  );
};

const BDPBelowTheFoldLayout: React.FC<{
  data: Omit<BundlePageProps["data"], "header" | "footer">;
  gt: Translated<GeneralTranslation>;
  reviewsT: ReviewTranslations;
  primaryButton: React.JSX.Element;
  headerState: HeaderPinnedState;
  deliveryPackageProps: NonEmptyArray<string> | undefined;
  longDescription: ExpandedRichText | undefined;
  BundleContents: React.ReactNode;
}> = ({ data, gt, reviewsT, primaryButton, headerState, deliveryPackageProps, longDescription, BundleContents }) => {
  const t = usePdpTranslations();
  const { locale } = usePageLocaleContext();

  const { contentArea, downloads } = data.components;

  const sections: PDPSections = [
    {
      label: t("navigation.aboveTheFoldContentHeadline"),
      anchor: "root",
      mobileHidden: false,
    },
    contentArea.first.length > 0
      ? {
          label: t("navigation.contentAreaHeadline"),
          hidden: true,
          node: (
            <>
              {renderNonEmpty(contentArea.first, components => (
                <>
                  <Spacer size={"md"} />
                  <ComponentRenderer
                    components={components}
                    localeForNumberFormat={locale}
                    translations={gt}
                    noSpaceForFirstComponent={true}
                  />
                </>
              ))}
            </>
          ),
        }
      : null,
    deliveryPackageProps || longDescription
      ? {
          label: gt("moreInfoLabel"),
          node: (
            <PDPDeliveryPackageDescription
              gt={gt}
              deliveryPackageProps={deliveryPackageProps}
              longDescription={longDescription}
            />
          ),
        }
      : undefined,
    {
      label: t("bundleContents.navigationLabel"),
      node: BundleContents ? (
        <>
          <Spacer size={"md"} />
          {BundleContents}
        </>
      ) : null,
    },
    renderNonEmpty(downloads, downloads => ({
      label: t("downloadsHeadline"),
      node: <ProductDetailPageDownloadsCarousel downloadsHeadline={t("downloadsHeadline")} downloads={downloads} />,
    })),
    renderNonEmpty(contentArea.second, components => ({
      label: t("navigation.secondContentAreaHeadline"),
      hidden: true,
      node: (
        <>
          <Spacer size={"md"} />
          <ComponentRenderer
            components={components}
            localeForNumberFormat={locale}
            translations={gt}
            noSpaceForFirstComponent={true}
          />
        </>
      ),
    })),
  ].filter(nonEmpty);

  return <PDPSectionsWithNavigation headerState={headerState} Button={primaryButton} sections={sections} />;
};
